/**
 * AdVenture Plus pricing — single source of truth for the /membership page,
 * the dashboard callout, and the server checkout endpoint. Amounts are
 * display copies of the Stripe prices (lookup keys are authoritative
 * server-side).
 *
 * THE LADDER (Isaac, 2026-09-09). Three stages on ONE subscription — the
 * free window is a Stripe trial on the founding price, never a separate
 * free product, so nobody is ever migrated between plans:
 *
 *   Stage 1  now → Oct 31       card on file, $0. Trial ends Sun Nov 1.
 *   Stage 2  Nov 1 → Nov 30     $19/mo founding rate, LOCKED FOR LIFE.
 *   Stage 3  Dec 1 onward       founding window shut; new joins pay $29/mo.
 *
 * The trial end is a FIXED CALENDAR DATE, not a rolling N days: everyone in
 * the free window converts on the same morning, the page can state a hard
 * deadline, and late joiners get a shorter (never longer) free ride. Someone
 * joining after Nov 1 simply starts billing immediately at whatever rate the
 * window allows.
 *
 * "Locked for life" is a real promise: a founding subscription stays on
 * pro_founding_monthly forever. Stage 3 changes what NEW buyers can select —
 * it never touches an existing subscription's price.
 */

/** Trial converts at midnight ET on Sunday Nov 1, 2026 (EDT, UTC-4). */
export const PRO_TRIAL_END = new Date("2026-11-01T04:00:00Z");

/** Founding window shuts at midnight ET on Tuesday Dec 1, 2026 (EST, UTC-5). */
export const PRO_FOUNDING_WINDOW_END = new Date("2026-12-01T05:00:00Z");

/**
 * Manual kill switch for the founding rate. Normally leave TRUE and let the
 * calendar close the window on its own — flip FALSE only to end it early.
 */
export const FOUNDING_RATE_MANUAL_OVERRIDE = true;

/**
 * The annual plan is built and priced in Stripe but NOT on sale during the
 * founding window (Patrick, 2026-09-30): $290/yr works out to about $24/mo,
 * which is a worse deal than the $19 founding rate, so showing it would only
 * confuse the offer. Flip TRUE to put it back on the page and in the sheet.
 * The server refuses an annual checkout while this is false.
 */
export const ANNUAL_PLAN_LIVE = false;

/** Is the founding rate still purchasable right now? */
export function foundingRateLive(now: Date = new Date()): boolean {
  return FOUNDING_RATE_MANUAL_OVERRIDE && now < PRO_FOUNDING_WINDOW_END;
}

/** Is the free trial window still open (i.e. does a new signup get $0 now)? */
export function trialWindowOpen(now: Date = new Date()): boolean {
  return now < PRO_TRIAL_END;
}

/**
 * Stripe Checkout rejects a `subscription_data.trial_end` less than 48 hours
 * out, which failed every signup from midnight ET Oct 30 (Amy's email,
 * 2026-10-30). Keep an hour of slack on top for clock skew.
 */
const STRIPE_MIN_TRIAL_MS = 49 * 60 * 60 * 1000;

/**
 * Stripe `trial_end` for a checkout starting now — unix seconds, or null
 * once the free window has closed (Stripe rejects a trial_end in the past).
 * In the window's last 48 hours it's pushed to 49 hours out, so a late
 * signup's first charge lands up to about a day after PRO_TRIAL_END.
 */
export function trialEndUnix(now: Date = new Date()): number | null {
  if (!trialWindowOpen(now)) return null;
  const end = Math.max(PRO_TRIAL_END.getTime(), now.getTime() + STRIPE_MIN_TRIAL_MS);
  return Math.floor(end / 1000);
}

/**
 * Back-compat alias. Older call sites imported a FOUNDING_RATE_LIVE constant;
 * the window is now date-driven, so they must call foundingRateLive().
 * @deprecated use foundingRateLive()
 */
export const FOUNDING_RATE_LIVE = FOUNDING_RATE_MANUAL_OVERRIDE;

export type ProPlan = "monthly" | "annual" | "founding_monthly";

export const PRO_PLANS: Record<
  ProPlan,
  { lookupKey: string; label: string; amountCents: number; interval: "month" | "year"; blurb: string }
> = {
  monthly: {
    lookupKey: "pro_monthly",
    label: "Monthly",
    amountCents: 2900,
    interval: "month",
    blurb: "$29 per month. Cancel anytime.",
  },
  annual: {
    lookupKey: "pro_annual",
    label: "Annual",
    amountCents: 29000,
    interval: "year",
    blurb: "$290 per year. Two months free compared to monthly.",
  },
  founding_monthly: {
    lookupKey: "pro_founding_monthly",
    label: "Founding Member",
    amountCents: 1900,
    interval: "month",
    blurb: "$19 per month, locked in for life. Founding window only.",
  },
};

/**
 * What the /membership page actually SHOWS. While the founding window is
 * open the standard monthly price is NOT visible — founding IS the monthly
 * option, anchored against the standard $29 ("discounted from" framing).
 * Once the window closes, standard monthly returns automatically.
 */
export function visibleProPlans(now: Date = new Date()): { plan: ProPlan; anchorCents?: number }[] {
  const plans: { plan: ProPlan; anchorCents?: number }[] = foundingRateLive(now)
    ? [{ plan: "founding_monthly", anchorCents: PRO_PLANS.monthly.amountCents }]
    : [{ plan: "monthly" }];
  if (ANNUAL_PLAN_LIVE) plans.push({ plan: "annual" });
  return plans;
}

/* ─── Date copy helpers — every surface renders the SAME words ──────────── */

const ET_LONG: Intl.DateTimeFormatOptions = {
  timeZone: "America/New_York",
  month: "long",
  day: "numeric",
};

/** "November 1" — the day the trial converts, in ET. */
export function trialEndLabel(): string {
  return new Intl.DateTimeFormat("en-US", ET_LONG).format(PRO_TRIAL_END);
}

/** "December 1" — the day the founding window shuts, in ET. */
export function foundingWindowEndLabel(): string {
  return new Intl.DateTimeFormat("en-US", ET_LONG).format(PRO_FOUNDING_WINDOW_END);
}

